/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React base styles
import { useState } from "react";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import Grid from "@mui/material/Grid";
import "./infocards.css";
import { ChangePassword } from "api-services";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { ShowErrorMessage } from "helpers";
import { useMaterialUIController } from "context";
import { ShowSuccessMessage } from "helpers";
import CustomPasswordField from "components/CustomPasswordField";

function ProfileInfoCard({ title, description, info, shadow }) {
  const labels = [];
  const values = [];

  const { loading } = useSelector((state) => state.authentication);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [showOTP, setShowOTP] = useState(false);

  // const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [errors, setErrors] = useState({});

  const dispatchData = useDispatch();
  const [, dispatch] = useMaterialUIController();

  var pascalize = function (str) {
    return str.replace(/\w+/g, function (w) {
      return w[0].toUpperCase() + w.slice(1).toLowerCase();
    });
  };

  // Convert this form `objectKey` of the object key in to this `object key`
  Object.keys(info).forEach((el) => {
    if (el.match(/[A-Z\s]+/)) {
      const uppercaseLetter = Array.from(el).find((i) => i.match(/[A-Z]+/));
      const newElement = el.replace(
        uppercaseLetter,
        ` ${uppercaseLetter.toLowerCase()}`
      );

      labels.push(newElement);
    } else {
      labels.push(el);
    }
  });

  // Push the object values into the values array
  Object.values(info).forEach((el) => values.push(el));

  // Render the card info items
  const renderItems = labels.map((label, key) => {
    if (values[key]) {
      return (
        <tr key={label} display="flex" py={1} pr={2} fullwidth>
          <td>
            <MDBox className="wrap-div">
              <MDTypography
                variant="button"
                fontWeight="bold"
                textTransform="capitalize"
              >
                {label}: &nbsp;
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                &nbsp;{values[key]}
              </MDTypography>
            </MDBox>
          </td>
        </tr>
      );
    }
  });

  const renderUpdateItems = labels.map((label, key) => (
    <MDBox key={label} mb={4} py={1} pr={2} className="wrap-input">
      <MDInput
        type="text"
        label={pascalize(label)}
        variant="outlined"
        value={values[key]}
        fullWidth
        className="inputbox"
      />
    </MDBox>
  ));

  const clearFields = () => {
    // setOldPassword("");
    // setNewPassword("");
    // setConfirmNewPassword("");
    setErrors();
  };

  const handleChangePassword = () => {
    setErrors();
    if (validatePassword()) {
      ChangePassword(
        dispatchData,
        {
          oldPassword: oldPassword,
          password: newPassword,
          twoFactorCode: twoFactorCode,
        },
        () => {
          if (!showOTP) {
            ShowSuccessMessage(
              dispatch,
              "Please enter the 6-digit code sent to your registered email address."
            );
            setShowOTP(true);
          } else {
            ShowSuccessMessage(
              dispatch,
              "Password Changed",
              "Password changed successfully!"
            );
            setShowOTP(false);
            setOldPassword("");
            setNewPassword("");
            setTwoFactorCode("");
          }
        },
        (err) => {
          setShowOTP(false);
          setOldPassword("");
          setNewPassword("");
          setTwoFactorCode("");
          ShowErrorMessage(dispatch, err);
        },
        clearFields
      );
    }
  };

  const validatePassword = () => {
    let tempErrors = {};
    let isValid = true;
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$^_+=!*()@%&]).{8,}$/;
    if (newPassword.length > 0 && !regex.test(newPassword)) {
      tempErrors["password_regex"] = true;
      isValid = false;
    }

    // if (confirmNewPassword.length > 0 && !regex.test(confirmNewPassword)) {
    //   tempErrors["confirm_password_regex"] = true;
    //   isValid = false;
    // }

    // if (newPassword != confirmNewPassword) {
    //   tempErrors["password_match"] = true;
    //   isValid = false;
    // }

    setErrors({ ...tempErrors });

    return isValid;
  };

  return (
    <Card
      sx={{
        height: "100%",
        width: "100%",
        boxShadow: !shadow && "none",
        display: "flex",
        flexDirection: "row",
      }}
      fullwidth
    >
      <Grid container>
        <Grid item xs={12} md={5} lg={4}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pt={2}
            px={2}
          >
            <MDTypography
              variant="h5"
              fontWeight="medium"
              textTransform="capitalize"
            >
              {title}
            </MDTypography>
            {/* <MDButton
            onClick={() => setShowUpdateForm(!showUpdateForm)}
            variant="text"
          >
            {
              showUpdateForm ? <Tooltip title="Close" placement="top">
                <Icon color="white">close</Icon>
              </Tooltip> :
                <Tooltip title="Edit" placement="top">
                  <Icon color="white">edit</Icon>
                </Tooltip>
            }

          </MDButton> */}
          </MDBox>
          <MDBox px={2}>
            <MDBox mb={2} lineHeight={1}>
              <MDTypography variant="button" color="text" fontWeight="light">
                {description}
              </MDTypography>
            </MDBox>
            <MDBox opacity={0.3}>
              <Divider />
            </MDBox>
            <MDBox>
              {showUpdateForm ? (
                renderUpdateItems
              ) : (
                <table>
                  <tbody>{renderItems}</tbody>
                </table>
              )}
            </MDBox>

            {showUpdateForm ? (
              <MDButton
                className="btn-update"
                variant="gradient"
                color="success"
              >
                Update Profile
              </MDButton>
            ) : (
              <></>
            )}
          </MDBox>
        </Grid>

        <Grid item px={2} xs={12} md={5} lg={3} className="profile">
          {!showOTP && (
            <>
              <CustomPasswordField
                label="Old Password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="inputbox"
              />
              <CustomPasswordField
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="inputbox"
                validationError={errors?.password_regex}
              />
            </>
          )}
          {showOTP && (
            <CustomPasswordField
              label="Enter Your OTP"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              className="inputbox mt-2 mb-2"
            />
          )}
          {errors?.password_match && (
            <MDTypography
              color="error"
              sx={{ fontSize: "14px", marginBottom: "5px" }}
            >
              Your Passwords Don't Match!
            </MDTypography>
          )}
          <MDButton
            className="btn-update"
            mt={2}
            mr={2}
            variant="gradient"
            color="success"
            onClick={handleChangePassword}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Change Password"
            )}
          </MDButton>
        </Grid>
      </Grid>
    </Card>
  );
}

// Setting default props for the ProfileInfoCard
ProfileInfoCard.defaultProps = {
  shadow: true,
};

// Typechecking props for the ProfileInfoCard
ProfileInfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  info: PropTypes.objectOf(PropTypes.string).isRequired,
  action: PropTypes.shape({
    route: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
  }).isRequired,
  shadow: PropTypes.bool,
};
export default ProfileInfoCard;
